import React from 'react';
import { cn } from '@repo/utils';
import { Button } from './Button';
import { ThemeToggle } from './ThemeToggle';
import logoFullUrl from '@repo/ui/assets/logo-full.svg';

interface SystemMessageLayoutProps {
  statusCode?: number | string;
  icon?: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'ghost';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'ghost';
  };
  className?: string;
}

export function SystemMessageLayout({
  statusCode,
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: SystemMessageLayoutProps) {
  return (
    <>
    <a href="/" className="fixed top-6 left-6 md:top-8 md:left-8 z-sticky flex items-center h-10">
      <img src={logoFullUrl} alt="Kraphene" className="h-7 w-auto object-contain" />
    </a>
    <div className="fixed top-6 right-6 md:top-8 md:right-8 z-sticky flex items-center h-10">
      <ThemeToggle />
    </div>
    <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,color-mix(in_srgb,var(--accent-500)_6%,transparent)_0%,transparent_70%)] pointer-events-none" />
    <div className="fixed inset-0 bg-bg tactical-grid pointer-events-none" />
    <div className="relative min-h-screen w-full text-text-primary flex items-center justify-center p-4">
      <div
        className={cn(
          'animate-[kraphene-fade-in_0.6s_ease-out,slide-up_0.6s_ease-out] flex flex-col items-center text-center max-w-md w-full gap-6',
          className
        )}
      >
        <div className="flex flex-col items-center gap-4">
          {statusCode && (
            <div className="text-[10rem] font-bold tnum text-text-primary/5 leading-none select-none absolute inset-x-0 top-0 -translate-y-1/4 pointer-events-none">
              {statusCode}
            </div>
          )}
          {icon && (
            <div className="text-text-muted opacity-60 mb-1">
              {icon}
            </div>
          )}
          <h1 className="text-display-lg font-semibold text-text-primary tracking-tight">
            {title}
          </h1>
          <p className="text-sm text-text-secondary leading-relaxed max-w-sm">
            {description}
          </p>
        </div>

        {(action || secondaryAction) && (
          <div className="mt-2 flex gap-3">
            {action && (
              <Button
                variant={action.variant || 'primary'}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant || 'secondary'}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
